import { toAlert, toDashboard } from '@gonfalon/navigator/navigators';
import type { IconName } from '@launchpad-ui/icons';

import { flagPagePath, parseFlagArtifactIdentity } from '../../../flagContext';
import { inferContextType, MENTION_MARKER_REGEX } from '../../../resources';
import type { ResourceOrQuery } from '../../../resources';

export interface ParsedMention {
  label: string;
  resourceType: string;
  id: string;
}

export interface ParsedMessageSegment {
  type: 'text' | 'mention';
  value: string;
  mention?: ParsedMention;
}

/**
 * Parse a message string into segments of plain text and mention markers.
 * Mention markers like @[CPU spike alert](Alert:123) are parsed into
 * structured objects with label, resourceType, and id.
 */
export function parseMessageMentions(message: string): ParsedMessageSegment[] {
  const segments: ParsedMessageSegment[] = [];
  let lastIndex = 0;

  for (const match of message.matchAll(new RegExp(MENTION_MARKER_REGEX, 'g'))) {
    const [fullMatch, label, resourceType, id] = match;
    const startIndex = match.index;

    if (startIndex > lastIndex) {
      segments.push({ type: 'text', value: message.slice(lastIndex, startIndex) });
    }

    segments.push({
      type: 'mention',
      value: fullMatch,
      mention: { label, resourceType, id },
    });

    lastIndex = startIndex + fullMatch.length;
  }

  if (lastIndex < message.length) {
    segments.push({ type: 'text', value: message.slice(lastIndex) });
  }

  return segments;
}

/** Short label for a resource — used in the @-mention suggestion list where the category is already shown as a header. */
export function getResourceLabel(resource: ResourceOrQuery): string {
  const payload = resource?.payload ?? {};
  return payload.name || payload.graph_title || payload.title || resource.id;
}

const truncate = (text: string, max = 60) => (text.length > max ? `${text.substring(0, max - 1)}…` : text);

/**
 * Type-aware display label for a resource. Used by both `ContextPills` and inline mention chips
 * (via `VegaMentionsInput.insertMention` and `MentionSuggestionPopover`) so attached resources
 * read the same whether they render as a pill at the top or as an inline chip in the editor.
 */
export function getContextLabel(resource: ResourceOrQuery): string {
  const payload = resource?.payload ?? {};
  const type = inferContextType(resource);

  switch (type) {
    case 'Sessions': {
      const sessionId = payload?.secure_id;
      const identifier = payload?.identifier;
      return `Session: ${identifier && identifier !== 'unknown' ? identifier : sessionId}`;
    }
    case 'Logs': {
      const level = (payload?.level || payload?.severity)?.toString().toUpperCase();
      const logMessage = payload?.message || 'Log entry';
      return level ? `${level} - ${truncate(logMessage)}` : truncate(logMessage);
    }
    case 'Errors': {
      const title = Array.isArray(payload.event) ? payload.event.join(' ') : 'Error';
      return title ? `${truncate(title)}` : 'Error';
    }
    case 'Traces': {
      const spanName = payload?.spanName || payload?.span_name;
      return spanName ? `${truncate(spanName)}` : 'Trace';
    }
    case 'Metrics':
      return 'Metrics';
    case 'Dashboard': {
      const name = payload?.dashboard_name || payload?.name;
      return name ? `Dashboard: ${truncate(name)}` : 'Dashboard';
    }
    case 'Graph': {
      const name = payload?.graph_title || payload?.title;
      return name ? `Graph: ${truncate(name)}` : 'Graph';
    }
    case 'Alert': {
      const name = payload?.name;
      return name ? `Alert: ${truncate(name)}` : 'Alert';
    }
    case 'SavedSearch': {
      const name = payload?.name;
      return name ? `Search: ${truncate(name)}` : 'Saved Search';
    }
    case 'ExternalSource': {
      const sourceType = typeof payload?.sourceType === 'string' ? payload.sourceType : undefined;
      const url = typeof payload?.url === 'string' ? payload.url : undefined;
      const labelByType: Record<string, string> = {
        slack: 'Slack',
        confluence: 'Confluence',
        jira: 'Jira',
        github: 'GitHub',
        atlas: 'Atlas',
      };
      const prefix = sourceType ? (labelByType[sourceType] ?? sourceType) : 'Source';
      if (!url) {
        return prefix;
      }
      try {
        const u = new URL(url);
        const tail = u.pathname.split('/').filter(Boolean).pop() ?? u.hostname;
        return `${prefix}: ${truncate(tail)}`;
      } catch {
        return `${prefix}: ${truncate(url)}`;
      }
    }
    case 'Flag': {
      const name = payload?.name || payload?.title || payload?.flag_key || payload?.flagKey;
      return name ? `Flag: ${truncate(String(name))}` : 'Flag';
    }
    default:
      return 'Context';
  }
}

export function getResourceTypeIconName(resourceType: string): IconName {
  switch (resourceType) {
    case 'Sessions':
      return 'play-circle';
    case 'Logs':
      return 'list';
    case 'Traces':
      return 'pulse';
    case 'Errors':
      return 'cancel-circle';
    case 'Metrics':
      return 'chart-line';
    case 'Dashboard':
      return 'chart-dashboard';
    case 'Graph':
      return 'chart-line';
    case 'Alert':
      return 'notifications';
    case 'SavedSearch':
      return 'search';
    case 'ExternalSource':
      return 'link-external';
    case 'Contexts':
      return 'person';
    case 'Flag':
      return 'flag-outline';
    default:
      return 'tag';
  }
}

/**
 * Builds a URL to view the referenced resource, or returns null
 * when the resource type doesn't have a navigable page.
 */
export function getMentionResourceUrl(projectKey: string, resourceType: string, id: string): string | null {
  switch (resourceType) {
    case 'Alert': {
      const route = toAlert({ projectKey, alertId: id });
      return route.pathname;
    }
    case 'Dashboard': {
      const route = toDashboard({ projectKey, dashboardId: id });
      return route.pathname;
    }
    case 'Flag': {
      const identity = parseFlagArtifactIdentity(id);
      if (!identity) {
        return null;
      }
      return flagPagePath(identity.projectKey, identity.flagKey);
    }
    default:
      return null;
  }
}
